import { BaseWebComponentController } from '../../../framework/BaseWebComponentController';
import { ExportExclusionsPanelWebComponent } from '../component/ExportExclusionsPanelWebComponent';
import { templateExportExclusionsPanel } from '../view/template';
import { ExportExclusionsView } from '../view/ExportExclusionsView';
import { GzDialogEvents } from '../../../ui/gzDialog/component/GzDialog';
import { GzFormFieldWebComponent } from '../../../ui/gzForm/formField';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import { ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { translateFn } from '../../../utils/translator.helper';

export class ExportExclusionsPanelController extends BaseWebComponentController {
  public component: ExportExclusionsPanelWebComponent;
  public view: ExportExclusionsView;
  public viewConstructor: typeof ExportExclusionsView;

  private csvHeader: string[];

  constructor(component: ExportExclusionsPanelWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: ExportExclusionsView,
      viewTemplate: templateExportExclusionsPanel,
    });
  }

  private static isHeaderValid(formField: GzFormFieldWebComponent): boolean {
    //force validation to happen before using the value
    formField.validate();
    return formField.Input.getFormValue() !== '' && formField.valid;
  }

  private static addslashes(text: string): string {
    return text
      .replace(/\\/g, '\\\\')
      .replace(/\u0008/g, '\\b')
      .replace(/\t/g, '\\t')
      .replace(/\n/g, '\\n')
      .replace(/\f/g, '\\f')
      .replace(/\r/g, '\\r')
      .replace(/'/g, "\\'")
      .replace(/"/g, '\\"');
  }

  async getDataForView(): Promise<any> {
    return {
      exclusions: this.dependencies.selectedExclusions,
    };
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$objectTypeFormField,
        event: 'change',
        callback: this.changeDataPreviewTableHeader.bind(this),
      },
      {
        element: this.view.dom.$excludedItemFormField,
        event: 'change',
        callback: this.changeDataPreviewTableHeader.bind(this),
      },
      {
        element: this.view.dom.$exclusionModulesFormField,
        event: 'change',
        callback: this.changeDataPreviewTableHeader.bind(this),
      },
      {
        element: this.view.dom.$saveButton,
        event: 'click',
        callback: this.saveExclusionsAsCsv.bind(this),
      },
      {
        element: this.view.dom.$cancelButton,
        event: 'click',
        callback: this.closeWindow.bind(this),
      },
    ];
  }

  private changeDataPreviewTableHeader() {
    const defaultDataPreviewTableHeader = this.view.getDefaultTableHeader();

    this.csvHeader = [
      ExportExclusionsPanelController.isHeaderValid(this.view.dom.$objectTypeFormField)
        ? this.view.dom.$objectTypeFormField.Input.value
        : defaultDataPreviewTableHeader[0],
      ExportExclusionsPanelController.isHeaderValid(this.view.dom.$excludedItemFormField)
        ? this.view.dom.$excludedItemFormField.Input.value
        : defaultDataPreviewTableHeader[1],
      ExportExclusionsPanelController.isHeaderValid(this.view.dom.$exclusionModulesFormField)
        ? this.view.dom.$exclusionModulesFormField.Input.value
        : defaultDataPreviewTableHeader[2],
    ];

    this.view.dom.$exclusionDataPreview.head = {
      header: this.csvHeader,
    };
  }

  private closeWindow() {
    this.component.dispatchEvent(
      new CustomEvent(GzDialogEvents.CANCEL, {
        bubbles: false,
        cancelable: true,
        composed: false,
      })
    );
  }

  private showExportSuccess() {
    this.closeWindow();
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: translateFn('Exclusions successfully exported.'),
      severity: ToastSeverity.SUCCESS,
    });
  }

  private saveExclusionsAsCsv() {
    let csvContent = 'data:text/csv;charset=utf-8,';

    for (const exclusion of this.dependencies.selectedExclusions) {
      csvContent +=
        '"' +
        exclusion.objectType +
        '","' +
        ExportExclusionsPanelController.addslashes(exclusion.excludedItems) +
        '","' +
        exclusion.modules +
        '"\n';
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', 'exclusions.csv');
    document.body.appendChild(link);

    link.click();
    document.body.removeChild(link);
    this.showExportSuccess();
  }
}
